/**
 * ShareModal - Modal with multiple share options for referral codes.
 * Supports copy link, email, SMS, Twitter/X, and Facebook sharing.
 */

import { useState, useCallback } from "react";
import { trackEvent } from "@jumpbean/analytics";
import type { ReferralShareChannel } from "@jumpbean/analytics";
import { Modal } from "../Modal";
import type { ReferralCode } from "../../hooks/useReferrals";

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  code: ReferralCode | null;
}

export function ShareModal({ isOpen, onClose, code }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const shareUrl = code?.shareUrl ?? "";
  const shareCode = code?.code ?? "";

  // Share messages for different channels
  const shareMessages = {
    email: {
      subject: "Try JumpBean - we love it!",
      body: `Hey!\n\nMy kids have been loving JumpBean - it's this app that teaches math, spelling, patterns, typing, and even coding basics through magical character adventures.\n\nUse my code ${shareCode} when you sign up and you'll get your first month free!\n\nHere's the link: ${shareUrl}`,
    },
    sms: `My kids love JumpBean! It teaches math, spelling & coding through play. Use my code ${shareCode} for a free month: ${shareUrl}`,
    twitter: `My kids are obsessed with @JumpBeanApp - magical characters that teach math, spelling, patterns, typing & coding! Use code ${shareCode} for a free month: ${shareUrl}`,
    facebook: `My kids love JumpBean! It's a magical app that teaches math, spelling, patterns, typing & coding. Get a free month with code ${shareCode}: ${shareUrl}`,
  };

  const trackShare = useCallback(
    (channel: ReferralShareChannel) => {
      if (shareCode) {
        trackEvent("referral_code_shared", { code: shareCode, channel });
      }
    },
    [shareCode]
  );

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackShare("link");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [shareUrl, trackShare]);

  const handleEmailShare = useCallback(() => {
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(shareMessages.email.subject)}&body=${encodeURIComponent(shareMessages.email.body)}`;
    window.open(mailtoUrl, "_blank");
    trackShare("email");
  }, [shareMessages.email.subject, shareMessages.email.body, trackShare]);

  const handleSmsShare = useCallback(() => {
    // Works on mobile devices
    const smsUrl = `sms:?body=${encodeURIComponent(shareMessages.sms)}`;
    window.open(smsUrl, "_blank");
    trackShare("sms");
  }, [shareMessages.sms, trackShare]);

  const handleTwitterShare = useCallback(() => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessages.twitter)}`;
    window.open(twitterUrl, "_blank", "width=600,height=400");
    trackShare("twitter");
  }, [shareMessages.twitter, trackShare]);

  const handleFacebookShare = useCallback(() => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareMessages.facebook)}`;
    window.open(facebookUrl, "_blank", "width=600,height=400");
    trackShare("facebook");
  }, [shareUrl, shareMessages.facebook, trackShare]);

  const shareOptions = [
    {
      name: "Copy Link",
      icon: LinkIcon,
      onClick: handleCopyLink,
      color: "from-neutral-100 to-neutral-200",
      border: "border-neutral-300",
      iconColor: "text-neutral-600",
      highlight: copied,
      highlightColor: "from-meadow-100 to-meadow-200",
      highlightBorder: "border-meadow-400",
      highlightText: "Copied!",
    },
    {
      name: "Email",
      icon: EmailIcon,
      onClick: handleEmailShare,
      color: "from-whiskers-100 to-whiskers-200",
      border: "border-whiskers-300",
      iconColor: "text-whiskers-600",
    },
    {
      name: "Text Message",
      icon: SmsIcon,
      onClick: handleSmsShare,
      color: "from-meadow-100 to-meadow-200",
      border: "border-meadow-300",
      iconColor: "text-meadow-600",
    },
    {
      name: "Twitter / X",
      icon: TwitterIcon,
      onClick: handleTwitterShare,
      color: "from-neutral-800 to-neutral-900",
      border: "border-neutral-700",
      iconColor: "text-white",
      textColor: "text-white",
    },
    {
      name: "Facebook",
      icon: FacebookIcon,
      onClick: handleFacebookShare,
      color: "from-blue-500 to-blue-600",
      border: "border-blue-600",
      iconColor: "text-white",
      textColor: "text-white",
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share Your Code"
      size="md"
    >
      <div className="space-y-4">
        {/* Code display */}
        <div className="text-center py-4 px-6 bg-gradient-to-br from-sunshine-50 to-sunshine-100 rounded-2xl border-[3px] border-sunshine-300">
          <p className="text-body-sm font-semibold text-neutral-500 uppercase tracking-wider mb-1">
            Your Code
          </p>
          <p className="font-mono font-bold text-display-sm text-neutral-900 tracking-widest">
            {shareCode}
          </p>
        </div>

        {/* Share options grid */}
        <div className="grid grid-cols-1 gap-3">
          {shareOptions.map((option) => (
            <button
              key={option.name}
              onClick={option.onClick}
              className={`
                flex items-center gap-4
                p-4 rounded-xl
                bg-gradient-to-br ${option.highlight ? option.highlightColor : option.color}
                border-[3px] ${option.highlight ? option.highlightBorder : option.border}
                transition-all duration-200
                hover:scale-[1.02] hover:shadow-md
                active:scale-[0.98]
              `}
            >
              <div
                className={`
                  w-12 h-12 rounded-full
                  flex items-center justify-center
                  bg-white/30
                  ${option.iconColor}
                `}
              >
                <option.icon className="w-6 h-6" />
              </div>
              <span
                className={`
                  font-display font-bold text-body-lg
                  ${option.textColor ?? "text-neutral-800"}
                `}
              >
                {option.highlight ? option.highlightText : option.name}
              </span>
              {option.highlight && (
                <div className="ml-auto">
                  <CheckIcon className="w-6 h-6 text-meadow-600" />
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Help text */}
        <p className="text-center text-body-sm text-neutral-500 pt-2">
          When friends subscribe with your code, you&apos;ll both get a free month!
        </p>
      </div>
    </Modal>
  );
}

// Icons
function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function SmsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default ShareModal;
